import type { CollapseProps } from "../../../utils/local-antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useTheme } from "../../../ThemeContext";
import { AtiraCollapse } from "../../../components/AtiraCollapse";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { Flex } from "../../../components/Flex";
import { WarningModal } from "../../../components/WarningModal";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { permissionActions } from "../../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { TestIDs } from "../../../tests/types/testIds";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { MemberPermissions } from "../components/MemberPermissions";
import { MemberRow } from "../components/MemberRow";
import { OwnerRow } from "../components/OwnerRow";
import { usePermissionsContext } from "../permissions-context";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { thresholdSliceSelectors } from "../../../redux/threshold/threshold.selector";

const Container = styled(Flex)`
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
  border-radius: ${Rounded.md};
  padding: ${Spacing.s};
  flex-direction: column;
`;

const StyledCollapseWrapper = styled.div`
  .ant-collapse-header {
    padding: 0 !important;
  }

  .ant-collapse-expand-icon {
    padding-inline-end: ${Spacing.s} !important;
    padding-inline-start: 0 !important;
  }

  .owner-panel .ant-collapse-expand-icon {
    visibility: hidden;
  }
`;

export const PermissionsMembersTab: React.FC = () => {
  const [removeMemberLoading, setRemoveMemberLoading] = useState(false);

  const { theme } = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    setInviteMemberModalVisible,
    setRemoveMemberModalVisible,
    removeMemberModalVisible,
    currentMember,
  } = usePermissionsContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );

  const canInvite =
    (thresholds?.consumed?.USERS || 0) < (thresholds?.total?.USERS || 0);

  const getInivteButtonTooltip = () => {
    if (canInvite) {
      return "";
    }

    if (thresholds?.total?.USERS === 1) {
      return t("permissions.invites.button.disabled-1");
    }

    return t("permissions.invites.button.disabled-2");
  };

  const onRemoveMember = async () => {
    try {
      setRemoveMemberLoading(true);

      await dispatch(
        permissionActions.removeMember({
          memberId: currentMember?._id!,
          userId,
        }),
      ).unwrap();
      await dispatch(permissionActions.getMembers({ userId })).unwrap();
      await dispatch(thresholdActions.getThresholds({ userId })).unwrap();

      setRemoveMemberModalVisible(false);
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setRemoveMemberLoading(false);
    }
  };

  const items = (): CollapseProps["items"] => {
    const panelStyle: React.CSSProperties = {
      borderBottom: "1px solid black",
      backgroundColor: theme.sub,
      border: "0",
    };

    const ownerItem = {
      key: "owner",
      className: "owner-panel",
      label: <OwnerRow />,
      style: panelStyle,
    };

    return [
      ownerItem,
      ...members.map((member) => ({
        key: `${member?._id}-permissions`,
        label: <MemberRow member={member} key={member._id} />,
        children: <MemberPermissions member={member} key={member._id} />,
        style: panelStyle,
      })),
    ];
  };

  return (
    <Container>
      <StyledCollapseWrapper>
        <AtiraCollapse
          collapsible="icon"
          items={items()}
          expandIconPosition="end"
        />
      </StyledCollapseWrapper>

      {!members.length ? (
        <AtiraEmpty
          description={t("permissions.members.empty")}
          action={{
            title: t("permissions.members.empty.action"),
            onClick: () => setInviteMemberModalVisible(true),
            dataTestId: TestIDs.permissions.teamRole.inviteOpen,
            disabled: !canInvite,
            tooltip: getInivteButtonTooltip(),
          }}
        />
      ) : null}

      <WarningModal
        isOpen={removeMemberModalVisible}
        title={t("premission.member.remove.modal_title", {
          name: currentMember?.name,
        })}
        description={t("premission.member.remove.modal_description")}
        onConfirm={onRemoveMember}
        onClose={() => setRemoveMemberModalVisible(false)}
        loading={removeMemberLoading}
        confirmButtonText={t("common.remove")}
        denyButtonText={t("common.cancel")}
      />
    </Container>
  );
};
